import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/site/Logo";
import { Field, inputCls } from "@/components/site/FormBits";
import { api, errMsg } from "@/lib/api";

const OtpLogin = ({ onDone }) => {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (!sent) {
        await api.post("/tech/otp/send", { phone });
        setSent(true);
      } else {
        await api.post("/tech/otp/verify", { phone, code });
        onDone();
      }
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={go} className="space-y-4" data-testid="tech-otp-form">
      <Field label="Mobile number"><Input required inputMode="tel" maxLength={14} value={phone} disabled={sent} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" className={inputCls} data-testid="tech-otp-phone" /></Field>
      {sent && <Field label="OTP"><Input required inputMode="numeric" maxLength={10} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={inputCls} autoComplete="one-time-code" data-testid="tech-otp-code" /></Field>}
      {err && <p className="text-sm text-destructive" data-testid="tech-otp-error">{err}</p>}
      <Button type="submit" disabled={busy} className="w-full rounded-full h-11" data-testid="tech-otp-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{sent ? "Verify & log in" : "Send OTP"}</Button>
      {sent && <button type="button" className="text-xs text-primary" onClick={() => { setSent(false); setCode(""); }} data-testid="tech-otp-change">Change number / resend</button>}
    </form>
  );
};

export default function TechLogin() {
  const nav = useNavigate();
  const from = useLocation().state?.from;
  const qc = useQueryClient();
  const [f, setF] = useState({ phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("password");
  const { data: otp } = useQuery({ queryKey: ["otp-status"], queryFn: async () => (await api.get("/tech/otp/status")).data, staleTime: 300000 });
  const done = () => {
    qc.removeQueries({ queryKey: ["tech"] });
    nav(from?.startsWith("/tech/jobs/") ? from : "/tech", { replace: true });
  };
  useEffect(() => { document.title = "Technician Login | Onkaal.com"; }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/tech/login", f);
      done();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center grain px-4">
      <div className="card-soft p-8 w-full max-w-sm space-y-5">
        <Logo className="h-10" />
        <div><h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="h-5 w-5 text-primary" />Technician Login</h1><p className="text-sm text-muted-foreground">For approved Onkaal service partners.</p></div>
        {otp?.enabled && (
          <div className="grid grid-cols-2 gap-1 rounded-full bg-muted p-1 text-sm" data-testid="tech-login-mode">
            {[["password", "Password"], ["otp", "Mobile OTP"]].map(([k, l]) => <button key={k} type="button" onClick={() => setMode(k)} className={`rounded-full py-1.5 font-semibold ${mode === k ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`} data-testid={`tech-login-mode-${k}`}>{l}</button>)}
          </div>
        )}
        {mode === "otp" && otp?.enabled ? <OtpLogin onDone={done} /> : (
      <form onSubmit={submit} className="space-y-5" data-testid="tech-login-form">
        <Field label="Mobile number"><Input required inputMode="tel" maxLength={14} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="10-digit mobile" className={inputCls} autoComplete="username" data-testid="tech-login-phone" /></Field>
        <Field label="Password"><Input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} autoComplete="current-password" data-testid="tech-login-password" /></Field>
        {err && <p className="text-sm text-destructive" data-testid="tech-login-error">{err}</p>}
        <Button type="submit" disabled={busy} className="w-full rounded-full h-11" data-testid="tech-login-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Log in</Button>
      </form>
        )}
        <div className="flex justify-between text-sm">
          <Link to="/tech/forgot-password" className="text-primary hover:underline" data-testid="tech-forgot-link">Forgot password?</Link>
          <Link to="/partner" className="text-muted-foreground hover:underline" data-testid="tech-register-link">Become a partner</Link>
        </div>
        <p className="text-xs text-muted-foreground">Your login password is shared by Onkaal after your application is approved.</p>
      </div>
    </div>
  );
}
